import * as THREE from 'three'
import Experience from '../Experience.js'

// Construit automatiquement la visite guidée à partir des données du
// parcours (départs et greens) : fenêtres de progression de chaque trou
// et un plan-séquence caméra par trou, adapté au départ choisi.
//
// Progression :
//   0 ............ intro (vue d'ensemble)
//   holesStart ... trou 1
//   ...            chaque trou occupe une fenêtre proportionnelle à sa
//                  longueur ; fondu au noir entre deux plans
//   holesEnd ..... outro (vue d'ensemble)
//   1
export default class CourseRoute {
    constructor() {
        this.experience = new Experience()
        this.resources = this.experience.resources
        this.terrain = this.experience.world.terrain
        this.config = this.experience.config.course
        this.timelineConfig = this.experience.config.camera.timeline

        this.indexCourse()
        this.holes = this.computeHoleWindows()
    }

    toWorld([geoX, geoY]) {
        return new THREE.Vector2(-geoX, -geoY)
    }

    indexCourse() {
        this.greens = new Map()
        for (const feature of this.resources.items.holes.features) {
            this.greens.set(feature.properties.hole, this.toWorld(feature.geometry.coordinates))
        }

        this.tees = new Map()
        for (const feature of this.resources.items.tees.features) {
            const { hole, tee } = feature.properties
            if (hole == null || !tee) continue
            if (!this.tees.has(hole)) this.tees.set(hole, new Map())
            this.tees.get(hole).set(tee, this.toWorld(feature.geometry.coordinates))
        }

        this.holeNumbers = [...this.greens.keys()]
            .filter(hole => this.tees.has(hole))
            .sort((a, b) => a - b)
    }

    getTee(hole, color) {
        const byColor = this.tees.get(hole)
        return byColor.get(color) ?? byColor.get('white') ?? byColor.values().next().value
    }

    // Départ « moyen » du trou : les fenêtres de progression ne dépendent
    // pas du départ choisi (la barre de progression existe avant le choix).
    getReferenceTee(hole) {
        const center = new THREE.Vector2()
        const tees = [...this.tees.get(hole).values()]
        for (const tee of tees) center.add(tee)
        return center.divideScalar(tees.length)
    }

    computeHoleWindows() {
        const { holesStart, holesEnd, baseWeight } = this.config
        const holes = this.holeNumbers

        const weights = holes.map((hole) => {
            const tee = this.getReferenceTee(hole)
            return tee.distanceTo(this.greens.get(hole)) + baseWeight
        })

        const total = weights.reduce((sum, w) => sum + w, 0)
        const span = holesEnd - holesStart
        const windows = {}

        let cursor = holesStart
        holes.forEach((hole, i) => {
            const start = cursor
            cursor += (weights[i] / total) * span
            const isLast = i === holes.length - 1
            windows[hole] = [
                +start.toFixed(4),
                +(isLast ? holesEnd : cursor - 0.001).toFixed(4)
            ]
        })

        return windows
    }

    ground(x, z) {
        return this.terrain.getSmoothHeightAt(x, z)
    }

    // Un plan-séquence par trou (plus l'intro et l'outro) : chaque segment
    // couvre une plage de progression et possède ses propres courbes de
    // position et de cible. Entre deux segments, la timeline fait un
    // fondu au noir et coupe.
    buildSegments(color = 'white') {
        const holes = this.holeNumbers
        const intro = this.timelineConfig.intro
        const outro = this.timelineConfig.outro
        const segments = []

        // Intro : lent travelling avant sur la vue d'ensemble
        const introPosition = new THREE.Vector3(intro.position.x, intro.position.y, intro.position.z)
        const introTarget = new THREE.Vector3(intro.target.x, intro.target.y, intro.target.z)
        segments.push(this.createSegment(0, this.config.holesStart, [
            { position: introPosition, target: introTarget },
            { position: introPosition.clone().lerp(introTarget, 0.18), target: introTarget }
        ]))

        holes.forEach((hole, i) => {
            const nextHole = holes[i + 1]
            const start = this.holes[hole][0]
            const end = nextHole ? this.holes[nextHole][0] : this.config.holesEnd
            segments.push(this.buildHoleSegment(hole, color, start, end))
        })

        // Outro : on s'élève doucement au-dessus du parcours
        const outroPosition = new THREE.Vector3(outro.position.x, outro.position.y, outro.position.z)
        const outroTarget = new THREE.Vector3(outro.target.x, outro.target.y, outro.target.z)
        segments.push(this.createSegment(this.config.holesEnd, 1, [
            { position: outroPosition.clone().lerp(outroTarget, 0.15), target: outroTarget },
            { position: outroPosition, target: outroTarget }
        ]))

        return segments
    }

    // Plan-séquence d'un trou, construit analytiquement pour être lisse
    // par construction (aucune courbe ne suit le relief point par point) :
    //  - trajectoire au sol : une seule courbe de Bézier, du dos du départ
    //    à l'avant du green, avec un unique arc latéral ;
    //  - hauteur : pente régulière entre le niveau du départ et celui du
    //    green, plus une bosse en sinus au milieu du survol ;
    //  - regard : glisse en ligne droite le long de l'axe du trou, sur la
    //    même pente régulière, jusqu'au drapeau ;
    //  - relief : si une colline passe trop près, une enveloppe de levée
    //    lisse est ajoutée (jamais de marche ni de rebond).
    buildHoleSegment(hole, color, start, end) {
        const c = this.config.camera
        const samples = this.config.samplesPerShot
        const tee = this.getTee(hole, color)
        const green = this.greens.get(hole)

        const length = tee.distanceTo(green)
        const dir = green.clone().sub(tee).normalize()
        const side = new THREE.Vector2(-dir.y, dir.x)
        const at = (along, lateral = 0) => tee.clone()
            .addScaledVector(dir, along)
            .addScaledVector(side, lateral)

        // Arc latéral d'un seul côté, alterné d'un trou à l'autre
        const arc = c.arc * Math.min(1, length / 1.5) * (hole % 2 ? 1 : -1)
        const greenBack = Math.min(c.greenBack, length * 0.3)
        const groundPath = new THREE.CubicBezierCurve(
            at(-c.teeBack),
            at(length * 0.3, arc),
            at(length * 0.72, arc),
            at(length - greenBack, arc * 0.35)
        )

        const teeGround = this.ground(tee.x, tee.y)
        const greenGround = this.ground(green.x, green.y)
        const baseAt = (along) => THREE.MathUtils.lerp(teeGround, greenGround, THREE.MathUtils.clamp(along / length, 0, 1))
        const boost = c.flyHeight - (c.teeHeight + c.greenHeight) / 2

        const positions = []
        const targets = []
        const deficits = new Float32Array(samples)

        for (let i = 0; i < samples; i++) {
            const s = i / (samples - 1)
            const xz = groundPath.getPointAt(s)
            const along = xz.clone().sub(tee).dot(dir)

            const height = THREE.MathUtils.lerp(c.teeHeight, c.greenHeight, s) + boost * Math.sin(Math.PI * s)
            const position = new THREE.Vector3(xz.x, baseAt(along) + height, xz.y)

            const lookAlong = THREE.MathUtils.lerp(
                Math.min(length * 0.55, c.teeLookAhead),
                length,
                THREE.MathUtils.smootherstep(s, 0.05, 0.8)
            )
            const lookXZ = at(lookAlong)
            const target = new THREE.Vector3(lookXZ.x, baseAt(lookAlong), lookXZ.y)

            positions.push(position)
            targets.push(target)
            deficits[i] = Math.max(0, this.ground(xz.x, xz.y) + c.clearance - position.y)
        }

        const lift = this.smoothEnvelope(deficits, Math.max(3, Math.round(samples * 0.08)))
        for (let i = 0; i < samples; i++) positions[i].y += lift[i]

        return {
            start,
            end,
            hole,
            positionCurve: new THREE.CatmullRomCurve3(positions),
            targetCurve: new THREE.CatmullRomCurve3(targets)
        }
    }

    // Descente d'ouverture en spirale : la caméra arrive du ciel au-dessus
    // des nuages et décrit un demi-tour large autour du point que regarde
    // le premier plan du trou 1, en se rapprochant et en descendant, pour
    // se poser exactement sur la première image de ce plan. Le regard reste
    // tourné vers ce point : à la fin du demi-tour, il est dans l'axe du
    // trou.
    buildFlyover(endPosition, endTarget) {
        const c = this.config.flyover
        const samples = 240
        const center = new THREE.Vector2(endTarget.x, endTarget.z)
        const end2 = new THREE.Vector2(endPosition.x, endPosition.z)

        const endRadius = end2.distanceTo(center)
        const endAngle = Math.atan2(end2.y - center.y, end2.x - center.x)
        const turn = Math.PI * (c.turns ?? 0.5) * 2 * (c.direction ?? 1)
        const lookPitch = THREE.MathUtils.degToRad(c.startPitch ?? 16)

        const positions = []
        const targets = []
        const deficits = new Float32Array(samples)

        for (let i = 0; i < samples; i++) {
            const s = i / (samples - 1)
            const remaining = 1 - s

            // Rayon et altitude décroissent vite au début, puis se posent
            // en douceur (dérivée nulle à l'arrivée)
            const angle = endAngle - turn * remaining
            const radius = endRadius + (c.startRadius - endRadius) * Math.pow(remaining, 1.5)
            const height = endPosition.y + (c.startHeight - endPosition.y) * remaining * remaining

            const x = center.x + Math.cos(angle) * radius
            const z = center.y + Math.sin(angle) * radius
            positions.push(new THREE.Vector3(x, height, z))

            // Regard : légèrement sous l'horizon au-dessus des nuages, puis
            // il plonge doucement vers le point central ; au milieu de la
            // spirale il plonge davantage pour garder les bords du terrain
            // hors du cadre, et se relève à l'approche du sol
            const highLook = height - radius * Math.tan(lookPitch)
            const dip = Math.pow(Math.sin(Math.PI * Math.min(1, s / 0.9)), 2) * radius * (c.midDip ?? 0.6)
            const lookY = THREE.MathUtils.lerp(highLook, endTarget.y, THREE.MathUtils.smootherstep(s, 0.1, 0.85)) - dip
            targets.push(new THREE.Vector3(center.x, lookY, center.y))

            deficits[i] = Math.max(0, this.ground(x, z) + this.config.camera.clearance - height)
        }

        // Garde au sol lissée (aucune remontée brusque au-dessus d'une colline)
        const lift = this.smoothEnvelope(deficits, 12)
        for (let i = 0; i < samples; i++) positions[i].y += lift[i]

        // Pose finale exacte : raccord parfait avec le plan du trou 1
        positions[samples - 1].copy(endPosition)
        targets[samples - 1].copy(endTarget)

        return {
            duration: c.duration,
            column: { start: center, end: center },
            positionCurve: new THREE.CatmullRomCurve3(positions),
            targetCurve: new THREE.CatmullRomCurve3(targets)
        }
    }

    // Plans simples (intro, outro) : courbe passant par quelques poses
    createSegment(start, end, keys) {
        return {
            start,
            end,
            hole: null,
            positionCurve: new THREE.CatmullRomCurve3(keys.map(k => k.position)),
            targetCurve: new THREE.CatmullRomCurve3(keys.map(k => k.target))
        }
    }

    // Enveloppe lisse majorant `values` : filtre max puis deux flous en
    // cascade. Le rayon du max (1,5 r) couvre la somme des rayons des flous
    // (r + r/2), ce qui garantit enveloppe >= valeur en tout point.
    smoothEnvelope(values, radius) {
        const count = values.length
        const pass = (input, r, reduce) => {
            const output = new Float32Array(count)
            for (let i = 0; i < count; i++) {
                let acc = 0
                let n = 0
                for (let k = i - r; k <= i + r; k++) {
                    // Bords : on prolonge la première / dernière valeur
                    const v = input[THREE.MathUtils.clamp(k, 0, count - 1)]
                    if (reduce === 'max') acc = Math.max(acc, v)
                    else acc += v
                    n++
                }
                output[i] = reduce === 'max' ? acc : acc / n
            }
            return output
        }

        const half = Math.max(1, Math.round(radius / 2))
        const widened = pass(values, radius + half, 'max')
        return pass(pass(widened, radius, 'mean'), half, 'mean')
    }
}
